import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import ImportConfig from "@mapmycustomers/shared/types/imports/ImportConfig";
import { Modal, TextField } from "@mapmycustomers/ui";

import { createConfig } from "@app/scene/settings/component/Imports/store/actions";
import {
  getImportConfigs,
  isCreatingConfig,
} from "@app/scene/settings/component/Imports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

interface Props {
  configs: ImportConfig[];
  createConfig: typeof createConfig.request;
  isCreating: boolean;
  okText?: string;
  onCancel?: () => void;
  onCreate?: () => void;
  onHide: () => void;
  visible: boolean;
}

const CreateConfigModal: React.FC<Props> = ({
  configs,
  createConfig,
  isCreating,
  okText,
  onCancel,
  onCreate,
  onHide,
  visible,
}) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();

  const [name, setName] = useState<string>("");
  const [error, setError] = useState<string | undefined>("");

  const handleSave = useCallback(() => {
    const formattedName = name.trim();
    if (formattedName.length === 0) {
      setError(
        intl.formatMessage({
          id: "settings.newImports.config.createConfigModal.nameField.error.empty",
          defaultMessage: "Please input name of new configuration",
          description: "Field is required error message for new import config",
        })
      );
    } else if (configs.some((config) => config.name.trim() === formattedName)) {
      setError(
        intl.formatMessage({
          id: "settings.newImports.config.createConfigModal.nameField.error.notUnique",
          defaultMessage: "Name of new configuration should be unique",
          description: "Field is required not Unique error message for new import config",
        })
      );
    } else {
      createConfig({
        callback: () => {
          onHide();
          onCreate?.();
          analyticIssuer?.clicked(["Save new Config"], { name });
        },
        name,
      });
    }
  }, [analyticIssuer, configs, createConfig, onCreate, onHide, name, intl, setError]);

  return (
    <Modal
      cancelButtonProps={{ hidden: !onCancel, onClick: onCancel }}
      okButtonProps={{ loading: isCreating }}
      okText={
        okText ??
        intl.formatMessage({
          id: "settings.newImports.config.createConfigModal.okButton",
          defaultMessage: "Save matching",
          description: "Imports save matching modal action",
        })
      }
      onCancel={onHide}
      onOk={handleSave}
      title={intl.formatMessage({
        id: "settings.newImports.config.createConfigModal",
        defaultMessage: "Save this matching",
        description: "Title of imports save matching modal",
      })}
      visible={visible}
    >
      <p>
        {intl.formatMessage({
          id: "settings.newImports.config.createConfigModal.subTitle",
          defaultMessage:
            "By saving this field matching, you can generate your field matchings in just a click the next time you import.",
          description: "SubTitle of imports save matching modal",
        })}
      </p>
      <TextField
        error={error}
        label={intl.formatMessage({
          id: "settings.newImports.config.createConfigModal.nameField",
          defaultMessage: "Name this saved field matching",
          description: "Imports save matching modal input title",
        })}
        onChange={setName}
        placeholder={intl.formatMessage({
          id: "settings.newImports.config.createConfigModal.nameField.placeholder",
          defaultMessage: "e.g. Quarterly Lead Import",
          description: "Imports save matching modal input Placeholder",
        })}
        size="large"
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  configs: getImportConfigs(state),
  isCreating: isCreatingConfig(state),
});

const mapDispatchToProps = {
  createConfig: createConfig.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateConfigModal);
